import * as Yup from 'yup'

// ============================================
// Form Validation Schemas
// ============================================

// Login form validation schema
export const loginSchema = Yup.object().shape({
  username: Yup.string()
    .required('Username or email is required')
    .min(3, 'Username must be at least 3 characters'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
})

// Forgot password form validation schema
export const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
})

// Reset password form validation schema
export const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  password_confirmation: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
})

// Change password form validation schema
export const changePasswordSchema = Yup.object().shape({
  current_password: Yup.string()
    .required('Current password is required'),
  password: Yup.string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
  password_confirmation: Yup.string()
    .required('Please confirm your new password')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
})

// Meta Business Profile form validation schema
export const metaBusinessProfileSchema = Yup.object().shape({
  display_name: Yup.string()
    .required('Display name is required')
    .max(255, 'Display name must be less than 255 characters'),
  external_profile_id: Yup.string()
    .required('Meta Business Profile ID is required')
    .max(255, 'Profile ID must be less than 255 characters'),
  access_token: Yup.string()
    .required('Access token is required'),
})

// Facebook Connect form validation schema
export const facebookConnectSchema = Yup.object().shape({
  display_name: Yup.string()
    .required('Display name is required')
    .max(255, 'Display name must be less than 255 characters'),
  external_profile_id: Yup.string()
    .required('Meta Business Profile ID is required')
    .max(255, 'Profile ID must be less than 255 characters'),
  access_token: Yup.string()
    .required('Access token is required'),
})

// Instagram Connect form validation schema
export const instagramConnectSchema = Yup.object().shape({
  display_name: Yup.string()
    .required('Display name is required')
    .max(255, 'Display name must be less than 255 characters'),
  external_profile_id: Yup.string()
    .required('Instagram Business Account ID is required')
    .max(255, 'Account ID must be less than 255 characters'),
  access_token: Yup.string()
    .required('Access token is required'),
})

// WhatsApp Connect form validation schema
export const whatsappConnectSchema = Yup.object().shape({
  display_name: Yup.string()
    .required('Display name is required')
    .max(255, 'Display name must be less than 255 characters'),
  external_profile_id: Yup.string()
    .required('WhatsApp Business Account ID is required')
    .max(255, 'Account ID must be less than 255 characters'),
  access_token: Yup.string()
    .required('Access token is required'),
})

// Telegram Connect form validation schema
export const telegramConnectSchema = Yup.object().shape({
  display_name: Yup.string()
    .required('Display name is required')
    .max(255, 'Display name must be less than 255 characters'),
  external_profile_id: Yup.string()
    .required('Telegram Bot Username is required')
    .max(255, 'Bot username must be less than 255 characters'),
  access_token: Yup.string()
    .required('Bot token is required'),
})

// LinkedIn Connect form validation schema
export const linkedinConnectSchema = Yup.object().shape({
  display_name: Yup.string()
    .required('Display name is required')
    .max(255, 'Display name must be less than 255 characters'),
  external_profile_id: Yup.string()
    .required('LinkedIn Company Page ID is required')
    .max(255, 'Page ID must be less than 255 characters'),
  access_token: Yup.string()
    .required('Access token is required'),
})

// Google Connect form validation schema
export const googleConnectSchema = Yup.object().shape({
  display_name: Yup.string()
    .required('Display name is required')
    .max(255, 'Display name must be less than 255 characters'),
  external_profile_id: Yup.string()
    .required('Google Business Profile ID is required')
    .max(255, 'Profile ID must be less than 255 characters'),
  access_token: Yup.string()
    .required('Access token is required'),
})

// ============================================
// Field-Level Validation Schemas
// ============================================

// Email validation
export const emailSchema = Yup.string()
  .email('Invalid email address')
  .required('Email is required')

// Password validation (basic - min 6 chars)
export const passwordSchema = Yup.string()
  .required('Password is required')
  .min(6, 'Password must be at least 6 characters')

// Strong password validation (with complexity requirements)
export const strongPasswordSchema = Yup.string()
  .required('Password is required')
  .min(6, 'Password must be at least 6 characters')
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    'Password must contain at least one uppercase letter, one lowercase letter, and one number'
  )

// URL validation
export const urlSchema = Yup.string()
  .url('Invalid URL format')
  .required('URL is required')

// Phone number validation (basic)
export const phoneSchema = Yup.string()
  .matches(/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/, 'Invalid phone number')
  .required('Phone number is required')

// ============================================
// Reusable Field Validators
// ============================================

// Required string validator
export const requiredString = (message = 'This field is required') =>
  Yup.string().required(message)

// Required number validator
export const requiredNumber = (message = 'This field is required') =>
  Yup.number().required(message).typeError('Must be a number')

// Optional string validator
export const optionalString = Yup.string()

// Optional email validator
export const optionalEmail = Yup.string().email('Invalid email address')

// Max length validator
export const maxLength = (max, message) =>
  Yup.string().max(max, message || `Must be less than ${max} characters`)

// Min length validator
export const minLength = (min, message) =>
  Yup.string().min(min, message || `Must be at least ${min} characters`)

// Positive number validator
export const positiveNumber = (message = 'Must be a positive number') =>
  Yup.number().positive(message).typeError('Must be a number')

// Integer validator
export const integerSchema = (message = 'Must be an integer') =>
  Yup.number().integer(message).typeError('Must be a number')

